import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type AnomalyBaselineReport, type AnomalyAlert } from '../api/client';
import { CaretLeft, CaretRight, MagnifyingGlass } from '@phosphor-icons/react';

const PAGE_SIZE = 25;

const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-error)' },
  medium: { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)' },
  low: { bg: 'rgba(96,165,250,0.12)', text: 'var(--color-info)' },
};

const SEVERITY_FILTERS = [
  { label: 'All', value: '' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const TYPE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Rate Spike', value: 'rate_spike' },
  { label: 'Error', value: 'error_rate' },
  { label: 'Latency', value: 'latency' },
  { label: 'New Tool', value: 'new_tool' },
  { label: 'Escalation', value: 'capability_escalation' },
  { label: 'Off Hours', value: 'off_hours' },
];

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function Anomalies() {
  const { currentWorkspaceId } = useAuth();
  const [report, setReport] = useState<AnomalyBaselineReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [severityFilter, setSeverityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    setLoading(true);
    api
      .getAnomalyBaseline(currentWorkspaceId)
      .then((data) => setReport(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWorkspaceId]);

  const filteredAlerts = useMemo(() => {
    let alerts = report?.alerts ?? [];
    if (severityFilter) alerts = alerts.filter((a) => a.severity === severityFilter);
    if (typeFilter) alerts = alerts.filter((a) => a.anomaly_type === typeFilter);
    if (search) {
      const sl = search.toLowerCase();
      alerts = alerts.filter((a) => a.entity_id.toLowerCase().includes(sl));
    }
    return alerts.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [report, severityFilter, typeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / PAGE_SIZE));
  const pageAlerts = filteredAlerts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Counts for stat cards
  const allAlerts = report?.alerts ?? [];
  const highCount = allAlerts.filter((a) => a.severity === 'high').length;
  const mediumCount = allAlerts.filter((a) => a.severity === 'medium').length;
  const lowCount = allAlerts.filter((a) => a.severity === 'low').length;

  if (!currentWorkspaceId) {
    return <p className="text-text-secondary">No workspace found. Complete onboarding first.</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg bg-[rgba(248,113,113,0.12)] p-4 text-sm text-[var(--color-error)]">{error}</div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Anomalies</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Behavioral anomalies detected across agents, tools, and workspace activity.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-border-dark bg-surface-1 p-4">
          <p className="text-2xl font-bold text-text-primary">{allAlerts.length}</p>
          <p className="text-xs text-text-secondary">Total Alerts</p>
        </div>
        <div className="rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] p-4">
          <p className="text-2xl font-bold text-[var(--color-error)]">{highCount}</p>
          <p className="text-xs text-[var(--color-error)]">High</p>
        </div>
        <div className="rounded-lg border border-[rgba(251,191,36,0.2)] bg-[rgba(251,191,36,0.12)] p-4">
          <p className="text-2xl font-bold text-[var(--color-warning)]">{mediumCount}</p>
          <p className="text-xs text-[var(--color-warning)]">Medium</p>
        </div>
        <div className="rounded-lg border border-[rgba(96,165,250,0.2)] bg-[rgba(96,165,250,0.12)] p-4">
          <p className="text-2xl font-bold text-[var(--color-info)]">{lowCount}</p>
          <p className="text-xs text-[var(--color-info)]">Low</p>
        </div>
      </div>

      {/* Filter chips: Severity */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-text-tertiary w-14 shrink-0">Severity</span>
          {SEVERITY_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setSeverityFilter(f.value); setPage(0); }}
              className="rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border"
              style={{
                backgroundColor: severityFilter === f.value ? 'var(--accent-teal)' : 'transparent',
                color: severityFilter === f.value ? 'var(--surface-0)' : 'var(--text-secondary)',
                borderColor: severityFilter === f.value ? 'var(--accent-teal)' : 'var(--border)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Filter chips: Type */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-text-tertiary w-14 shrink-0">Type</span>
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setTypeFilter(f.value); setPage(0); }}
              className="rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border"
              style={{
                backgroundColor: typeFilter === f.value ? 'var(--accent-teal)' : 'transparent',
                color: typeFilter === f.value ? 'var(--surface-0)' : 'var(--text-secondary)',
                borderColor: typeFilter === f.value ? 'var(--accent-teal)' : 'var(--border)',
              }}
            >
              {f.label}
            </button>
          ))}

          {/* Search on the right */}
          <div className="ml-auto relative">
            <MagnifyingGlass
              size={14}
              weight="duotone"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search entity..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="rounded-md border pl-8 pr-3 py-1.5 text-xs w-48 outline-none transition-colors focus:border-[var(--accent-teal)]"
              style={{
                backgroundColor: 'var(--surface-0)',
                borderColor: 'var(--border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border-dark bg-surface-1">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner-gradient h-6 w-6" />
          </div>
        ) : pageAlerts.length === 0 ? (
          <div className="p-8 text-center text-text-secondary">
            <p className="font-medium">No anomalies found</p>
            <p className="text-sm mt-1">
              {severityFilter || typeFilter || search
                ? 'No alerts match the current filters.'
                : 'No behavioral anomalies have been detected.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border-dark bg-surface-0">
                  <th className="px-4 py-3 font-medium text-text-secondary">Time</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Type</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Entity</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Metric</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Current vs Baseline</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Z-Score</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {pageAlerts.map((alert) => (
                  <AnomalyRow key={alert.alert_id} alert={alert} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredAlerts.length > 0 && (
          <div className="flex items-center justify-between border-t border-border-dark px-4 py-3">
            <span className="text-xs text-text-tertiary">
              Page {page + 1} of {totalPages} ({filteredAlerts.length} alerts)
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
              >
                <CaretLeft size={12} weight="bold" /> Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
              >
                Next <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AnomalyRow({ alert }: { alert: AnomalyAlert }) {
  const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.low;
  return (
    <tr className="hover:bg-surface-2">
      <td className="whitespace-nowrap px-4 py-3 text-text-secondary text-xs">
        {new Date(alert.timestamp).toLocaleString()}
      </td>
      <td className="px-4 py-3">
        <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs text-text-primary">
          {titleCase(alert.anomaly_type)}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-text-primary font-mono">{alert.entity_id}</div>
        <div className="text-[10px] text-text-tertiary">{alert.entity_type}</div>
      </td>
      <td className="px-4 py-3 text-xs text-text-secondary">{alert.metric}</td>
      <td className="px-4 py-3 text-xs tabular-nums">
        <span className="text-text-primary font-medium">{alert.current_value.toFixed(1)}</span>
        <span className="text-text-tertiary"> vs </span>
        <span className="text-text-secondary">{alert.baseline_mean.toFixed(1)}</span>
      </td>
      <td className="px-4 py-3 text-xs tabular-nums text-text-secondary">
        {alert.z_score.toFixed(2)}
      </td>
      <td className="px-4 py-3">
        <span
          className="rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {alert.severity}
        </span>
      </td>
    </tr>
  );
}
